import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap/esm/index";
import Helmet from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";
import { pushOutboundClick } from "../../assets/datalayerFunctions";

// Import Components
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import Accordion from "../../components/shared-ui/Accordion/Accordion";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import YoutubeEmbed from "../../components/shared-ui/Video/YoutubeEmbed";
import PrimaryButton from "../../components/shared-ui/Buttons/PrimaryButton";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";

import "../../css/SignsAndSymptoms.scss";

function showModal(url) {
  let modal = document.querySelector("#modal");
  document.body.classList.add("no-scroll", "modal-open");
  modal.classList.remove("fadeOut");
  modal.style.display = "block";
  modal.classList.add("fadeIn");
  modal.style.height = "100%";
  modal.children[0].children[1].children[3].href = url;
  modal.children[0].children[1].children[3].target = "_blank";
  modal.children[0].children[1].children[3].addEventListener(
    "click",
    function () {
      modal.style.display = "none";
    }
  );
}

function SignsAndSymptoms() {
  function addModalLinks(event) {
    pushOutboundClick(event);
    event.preventDefault();
    let url = event.currentTarget.href;
    let title = event.currentTarget.title;
    showModal(url, title);
  }
  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "Signs & Symptoms | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);

  const showPhreesiaModal = (event) => {
    let modal = document.querySelector("#phreesia-modal");
    document.body.classList.add("no-scroll");
    modal.classList.remove("fadeOut");
    modal.style.display = "block";
    modal.classList.add("fadeIn");
    modal.style.height = "100%";
    modal.querySelector('.exitBtn').addEventListener(
      "click",
      function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      }
    );
    modal.querySelector('.modal-overlay').addEventListener(
      "click",
      function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      }
    );
  };

  return (
    <div className="main_content_wrap">
      <Helmet>
        <title>
          Signs & Symptoms of Immune Thrombocytopenia (ITP) | DOPTELET®
          (avatrombopag)
        </title>
        <meta
          name="description"
          content="Learn about chronic immune thrombocytopenia (ITP) signs and symptoms like fatigue, low platelets and bruising, and how DOPTELET® can help."
        />
        <link
          rel="canonical"
          href="https://doptelet.com/what-is-itp/itp-signs-and-symptoms/"
        />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1>Signs &amp; Symptoms OF Immune Thrombocytopenia (ITP)</h1>
              <p className="mb-0">
                It’s important to understand the signs and symptoms of ITP. Knowing what to look for, and tracking any changes, can help you make informed treatment decisions with your doctor.
              </p>
            </>
          }
          maxWidth="480"
          bgImage={["signs-symptoms-hero.jpg", "signs-symptoms-hero.jpg"]}
          bgImageMobile={[
            "signs-symptoms-hero-mobile.jpg",
            "signs-symptoms-hero-mobile@2x.jpg",
          ]}
          imageAlt="Doptelet characters playing games in carnival setting"
        />

        <section className="section-padding pb-0">
          <Container>
            <Row className="align-items-center">
              <Col md={{ span: 12 }}>
                <div>
                  <h2>What are the common signs and symptoms of ITP?</h2>
                  <p>ITP is known as a heterogeneous disease, which means you may experience different signs and symptoms than someone else with ITP.</p>
                  <p>A sign is something that your healthcare team can observe or measure, such as your platelet counts. Symptoms are the feelings and concerns that you experience, such as fatigue. Most people with ITP have platelet counts under 100,000 per microliter, but those counts vary from person to person, and you may find your platelet counts are unpredictable at times. The lower your platelet count, the greater the risk of ITP symptoms.</p>
                  <p><strong>Here are some common signs and symptoms of ITP:</strong></p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="symptoms-itp">
          <Container>
            <div className="orange-border">
              <Row className="ml-md-0 mr-md-0">
                <Col md={12} lg={6} className="pl-md-0">
                  <div className="d-flex align-items-center mb-2">
                    <img
                      src="/images/icon_fatigue.svg"
                      alt="Low battery icon representing fatigue as an ITP symptom"
                      className="mr-3"
                      style={{maxWidth: 64}}
                    />
                    <h3 className="mb-0"><strong>Fatigue</strong></h3>
                  </div>
                  <p className="mb-4 mb-lg-0">You may feel exhausted and unable to keep up with everyday activities to the point where you may feel tired even after getting a full night’s sleep.</p>
                </Col>
                <Col md={12} lg={6} className="pr-md-0">
                  <div className="d-flex align-items-center mb-2">
                    <img
                      src="/images/icon_bruising.svg"
                      alt="Bruising easily icon representing a symptom of ITP"
                      className="mr-3"
                      style={{maxWidth: 64}}
                    />
                    <h3 className="mb-0"><strong>Bruising</strong></h3>
                  </div>
                  <p className="mb-4 mb-lg-0">You may notice your skin start to bruise very easily, even spontaneously, from damaged blood vessels under the skin. Bruising may happen even if you don’t remember injuring yourself.</p>
                </Col>
              </Row>
              <Row className="ml-md-0 mr-md-0">
                <Col md={12} lg={6} className="pl-md-0">
                  <div className="d-flex align-items-center mb-2">
                    <img
                      src="/images/icon_petechiae.svg"
                      alt="Petechiae or purpura on hand icon representing a symptom of ITP"
                      className="mr-3"
                      style={{maxWidth: 64}}
                    />
                    <h3 className="mb-0"><strong>Petechiae/Purpura</strong></h3>
                  </div>
                  <p className="mb-4 mb-lg-0">You may see small red or purple spots start to appear from broken blood vessels on areas like your upper arms or lower legs.</p>
                </Col>
                <Col md={12} lg={6} className="pr-md-0">
                  <div className="d-flex align-items-center mb-2">
                    <img
                      src="/images/icon_bleeding.svg"
                      alt="Drops of blood icon representing ITP bleeding symptoms"
                      className="mr-3"
                      style={{maxWidth: 64}}
                    />
                    <h3 className="mb-0"><strong>ITP Bleeding</strong></h3>
                  </div>
                  <p className="mb-0">You may experience bleeding from your gums and nose, have an unusually heavy menstrual flow, or notice cuts bleeding for longer periods of time. You may also see blood in your urine, vomit, or stool. When platelets are low, your doctor may tell you to limit physical activity.</p>
                </Col>
              </Row>
            </div>
          </Container>
        </section>

        <section className="section-padding pt-5 pb-5">
          <Container>
            <Row>
              <Col>
                <h3 className="mb-3"><strong>Living with ITP can be unpredictable</strong></h3>
                <p className="text-blue mb-0"><strong>You may feel stress and worry related to your signs and symptoms of ITP. Check out these resources that may help you navigate and manage your uncertainties.</strong></p>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding pt-0">
          <Container>
            <Row>
              <Col md={{ span: 12 }}>
                <div className="contained orange-bg d-flex align-items-center flex-column">
                    <h2>Find a chronic ITP specialist near you</h2>
                    <p className="mb-0">
                      One of the most important steps to getting the care you deserve is finding the right hematologist in your area.
                    </p>
                    <PrimaryButton
                      title="FIND A SPECIALIST"
                      addClass="btn btn-primary mb-4 mt-4"
                      onClick={showPhreesiaModal}
                    />
                    <p className="mb-0">Sobi is not responsible for the choices or actions that you or others take because of information provided by this free tool.</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="cloud-blue-bg section-padding my-florio">
          <Container>
            <Row>
              <Col md={12} lg={8} className="align-content-center">
                <h2>Keep your ITP journey on track with my florio ITP™</h2>
                <picture>
                  <source
                    srcSet="/images/MyFlorioLogo.jpg"
                    type="image/jpg"
                  />
                  <img
                    src="/images/MyFlorioLogo.jpg"
                    alt="Doptelet Florio ITP platelet tracker app icon"
                    className="d-lg-none d-block m-auto"
                  />
                </picture>
                <p>my florio ITP™ is an all-in-one app designed to monitor all things ITP—platelet counts, bruising events, appointments, treatments, and more—plus, it’s free!</p>
                <p>Get started with my florio ITP™ by downloading the app through the Apple Store or Google Play.</p>
                <div className="d-flex">
                  <a
                    href="https://apps.apple.com/no/app/florio-itp/id1610121401"
                    onClick={addModalLinks}
                    target="_blank"
                    className="app-link">
                    <img
                      src="/images/app-store.png"
                      alt="Apple app store icon"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=app.florio.itp.eu.mdd&pli=1"
                    onClick={addModalLinks}
                    target="_blank"
                    className="app-link">
                    <img
                      src="/images/google-play.png"
                      alt="Google Play icon"
                    />
                  </a>
                </div>
              </Col>
              <Col md={12} lg={4} className="d-none d-lg-block">
                <picture>
                  <source
                    srcSet="/images/MyFlorioLogo.png"
                    type="image/png"
                  />
                  <img
                    src="/images/MyFlorioLogo.png"
                    alt="Doptelet Florio ITP platelet tracker app icon"
                    className="m-auto d-none d-md-block"
                  />
                </picture>
              </Col>
            </Row>
          </Container>
        </section>

        <LookingForSupport addClass="section-padding" />

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div>
  );
}

export default SignsAndSymptoms;
